import { FileTextOutlined } from '@ant-design/icons';
import { getGetFrontRichiesteIdModuliDelegheQueryKey } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumInfoDelegaTipo } from '@framework/api-client/api/inquiries-compile/schemas/enumInfoDelegaTipo.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { SubmitButton } from '@framework/form/form/submit/SubmitButton.tsx';
import { type IModuleDelegation } from '@framework/frontoffice-submission/module/delegation/DelegationLogic.ts';
import { getModuleTitle } from '@framework/frontoffice-submission/module/getModuleTitle.ts';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { DrawerButtonRow } from '@framework/ui/components/drawer/DrawerButtonRow.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { DividerTitleElement } from '@framework/ui/components/text/DividerTitleElement.tsx';
import { Button, Divider, List, message, Space, Typography } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { queryClient } from '../../../../core/network/queryClient.ts';
import { useHandleModulesDelegationsSubmit } from '../hooks/useHandleModulesDelegationsSubmit.ts';
import { useModuleDelegation } from '../hooks/useModuleDelegation.ts';
import { DelegationTypeUserTag } from './DelegationTypeUserTag.tsx';
import { EditModulesDelegationsSchema } from './EditModulesDelegationsSchema.ts';
import { ModulesDelegationInput } from './ModulesDelegationInput.tsx';
import { ModulesViewDelegationsInput } from './ModulesViewDelegationsInput.tsx';

const StyledDivider = styled(Divider)`
  &.ant-divider {
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;

const Text = Typography.Text;

export interface IEditModulesDelegationsDrawerProps {
  submissionId: string;
  modulesDelegations: IModuleDelegation[];
  allDelegations: IModuleDelegation[];
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function EditModulesDelegationsDrawer(
  props: IEditModulesDelegationsDrawerProps,
) {
  const { open, setOpen, modulesDelegations, submissionId, allDelegations } =
    props;

  const { t } = useTranslation();

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const { onSubmit } = useHandleModulesDelegationsSubmit({
    submissionId,
    allDelegations,
  });

  const {
    initialValues,
    compilation,
    sign,
    view,
    isDifferent,
    hasAtleastOneSignableModule,
  } = useModuleDelegation(modulesDelegations);

  return (
    <Drawer
      title={t(
        'delegation.modifica-deleghe-moduli-drawer.titolo',
        'Gestione Deleghe Moduli',
      )}
      open={open}
      onClose={handleClose}
      destroyOnClose
      size="large"
    >
      <FormHandler
        nested
        layout="vertical"
        schema={EditModulesDelegationsSchema}
        initialValues={initialValues}
        onSubmit={async values => {
          const moduleIds = modulesDelegations
            .map(m => m.submissionModule.id)
            .filter(Boolean) as string[];
          try {
            await onSubmit(values, moduleIds);
            await queryClient.invalidateQueries(
              getGetFrontRichiesteIdModuliDelegheQueryKey(submissionId),
            );
            message.success(
              t(
                'delegation.modifica-deleghe-moduli-drawer.deleghe-aggiornate',
                'Deleghe aggiornate con successo',
              ),
            );
            setOpen(false);
          } catch (e) {
            console.error(e);
            // biome-ignore format: <tbd>
            message.error(getNetworkErrorMessage(e, t("delegation.errore", 'Si è verificato un errore. Riprovare.')));
          }
        }}
      >
        {/* Lista moduli selezionati */}
        <DividerTitleElement
          title={t(
            'delegation.modifica-deleghe-moduli-drawer.moduli-selezionati',
            'Moduli Selezionati',
          )}
          removeTopMargin
        />

        <List bordered>
          {modulesDelegations.map(moduleDelegation => (
            <List.Item key={moduleDelegation.submissionModule.id}>
              <Space>
                <FileTextOutlined aria-hidden />
                <Text strong>
                  {getModuleTitle(moduleDelegation.submissionModule)}
                </Text>
              </Space>
            </List.Item>
          ))}
        </List>

        {/* Gestione delegati */}
        <DividerTitleElement
          title={t(
            'delegation.modifica-deleghe-moduli-drawer.delegati',
            'Delegati',
          )}
        />

        <div>
          <ModulesDelegationInput
            icon={
              <DelegationTypeUserTag
                delegate={{
                  type: EnumInfoDelegaTipo.COMPILAZIONE,
                  userName: t(
                    'delegation.modifica-deleghe-moduli-drawer.delega-di-compilazione',
                    'Delega di Compilazione',
                  ),
                }}
              />
            }
            name="compilation"
            label={t(
              'delegation.modifica-deleghe-moduli-drawer.delega-di-compilazione',
              'Delega di Compilazione',
            )}
            currentValue={compilation}
            needConfirm={isDifferent.compilation}
          />
        </div>
        {hasAtleastOneSignableModule && (
          <>
            <StyledDivider />
            <div>
              <ModulesDelegationInput
                icon={
                  <DelegationTypeUserTag
                    delegate={{
                      type: EnumInfoDelegaTipo.FIRMA,
                      userName: t(
                        'delegation.modifica-deleghe-moduli-drawer.delega-di-firma',
                        'Delega di Firma',
                      ),
                    }}
                  />
                }
                name="sign"
                label={t(
                  'delegation.modifica-deleghe-moduli-drawer.delega-di-firma',
                  'Delega di Firma',
                )}
                currentValue={sign}
                needConfirm={isDifferent.sign}
                disabled={!hasAtleastOneSignableModule}
              />
            </div>
          </>
        )}
        <StyledDivider />
        <div>
          <ModulesViewDelegationsInput
            name="view"
            label={t(
              'delegation.modifica-deleghe-moduli-drawer.deleghe-di-visualizzazione',
              'Deleghe di Visualizzazione',
            )}
            uniqueDelegations={view}
            needConfirm={isDifferent.view}
          />
        </div>

        <VerticalSpace size={48} />

        <DrawerButtonRow>
          <Space>
            <Button size="large" onClick={handleClose}>
              {t('common.annulla', 'Annulla')}
            </Button>
            <SubmitButton />
          </Space>
        </DrawerButtonRow>
      </FormHandler>
    </Drawer>
  );
}
